// @ts-strict-ignore
import { IArticleInsertProcessor } from './i-article-insert.processor';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleVO } from '@article/model/vo/article.vo';
import { ArticleLocationVO } from '@article/model/vo/article-location.vo';
import { Line } from '@3d-scene/model/line';
import { ConnectionAreaVO } from '@shared/vo/meta-element.vo';
import { Connection } from '@3d-scene/model3d/connection';
import { ProjectModel } from '@project/model/project.model';
import { ProjectHelper } from '@project/helper/project.helper';
import { ArticleContainersWrapper } from '@3d-scene/model3d/article-containers-wrapper';
import { ArticleTypeConstants } from '@article/constants/article-type-constants';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { Rectangle3D } from '@3d-scene/model/rectangle-3d';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { Matrix4, Vector3 } from 'three';
import { CompositionContainer } from '@3d-scene/model3d/composition-container';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { NumberUtils } from '@shared/util/number-utils';
import { ArticleUsagePlausiPropertyDef } from '@article/constants/article-usage-plausi-property-def';
import { ConnectArticleHelper } from '@article/helper/connect-article.helper';
import { RotateArticlePresenter } from '../../../presenter/rotate-article.presenter';
import { GlobalContainer } from '@3d-scene/model3d/global-container';
import { UserModel } from '@user/model/user.model';
import { ArticleContainerFactory } from '@3d-scene/model3d/article-container.factory';
import { ArticleManager } from '@article/manager/article.manager';
import { CompositeArticleCacheManager } from '@article/manager/composite-article-cache.manager';
import { CompositeArticleUtils } from '@article/util/composite-article-utils';
import { ArticleContainerLoader } from '@article/service/article-container-loader';
import { InsertArticleHelper } from '@article/helper/insert-article.helper';
import { AttachedArticleContainer } from '../../../service/article-container-attached.service';
import { ProjectModelService } from '@project/service/project-model.service';
import { ProjectArticleService } from '@project/service/project-article.service';
import { LineComponentUtils } from '@3d-scene/model3d/util/line-component-utils';
import { ProjectMetaElementService } from '@project/manager/project-meta-element.service';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';
import { ConnectionInfoService } from '@3d-scene/model3d/service/connection-info.service';
import { AddArticleError } from '../../add-article-error';

export abstract class BaseArticleInsertProcessor implements IArticleInsertProcessor {

  constructor(
    protected userModel: UserModel,
    protected globalContainer: GlobalContainer,
    protected projectModel: ProjectModel,
    protected projectModelService: ProjectModelService,
    protected projectArticleService: ProjectArticleService,
    protected projectMetaElementService: ProjectMetaElementService,
    protected projectHelper: ProjectHelper,
    protected localeResourceManager: LocaleResourceManager,
    protected rotateArticlePresenter: RotateArticlePresenter,
    protected containerService: ContainerService,
    protected lineComponentUtils: LineComponentUtils,
    protected connectionInfoService: ConnectionInfoService,
    protected articleContainerLoader: ArticleContainerLoader,
    protected compositeArticleCacheManager: CompositeArticleCacheManager,
    protected articleContainerFactory: ArticleContainerFactory,
    protected articleManager: ArticleManager,
    protected compositeArticleUtils: CompositeArticleUtils,
    protected articleContainerUtils: ArticleContainerUtils,
    protected detachArticleUsageService: DetachArticleUsageService,
  ) {}

  public abstract findGivingCubeByConnectionArea(
    containers: ArticleContainer[],
    baseContainer: ArticleContainer,
    connectionArea: ConnectionAreaVO
  ): Connection;

  public createArticleContainersWrapper(article: ArticleVO, articleLocation: ArticleLocationVO,
                                        insertArticleHelper: InsertArticleHelper): ArticleContainersWrapper {
    const articlesWrapper: ArticleContainersWrapper = new ArticleContainersWrapper(
      article,
      null,
      ArticleTypeConstants.EXCLUDED_TYPES_FROM_COMPOSITE_ARTICLE,
      this.projectModel,
      this.projectArticleService,
      this.articleContainerLoader,
      this.compositeArticleCacheManager,
      this.articleContainerFactory,
      this.articleManager,
      this.articleContainerUtils,
      this.detachArticleUsageService
    );
    articlesWrapper.articleUsageID = articleLocation.existingArticleUsageId;
    articlesWrapper.isArticleOnRail = true;
    return articlesWrapper;
  }

  public process(
    containers: ArticleContainer[],
    baseContainers: ArticleContainer[],
    articleLine: Line,
    acceptingConnectionCube: Connection,
    givingConnectionCubes: Connection[],
    rotationAngle: number,
    excludedUsageIds: string[] = null,
    isMessageDispatchRequired: boolean = true
  ): void {
    if (
      givingConnectionCubes &&
      givingConnectionCubes.length > 0 &&
      givingConnectionCubes[0].compositionRectangle &&
      acceptingConnectionCube &&
      acceptingConnectionCube.compositionRectangle
    ) {
      for (const givingCube of givingConnectionCubes) {
        this.projectMetaElementService.createUsageBetweenConnections(givingCube, acceptingConnectionCube);
      }
      this.articleContainerUtils.updateActiveSetInArticleContainers(baseContainers);
      this.connectArticleOnRail(
        containers,
        baseContainers,
        articleLine,
        acceptingConnectionCube,
        givingConnectionCubes[0],
        rotationAngle,
        excludedUsageIds
      );
      if (isMessageDispatchRequired) {
        this.dispatchMessages(containers, acceptingConnectionCube);
      }
    } else {
      throw new AddArticleError(this.localeResourceManager.getValueByKey('connectProcessor.message.unknown_connection', [givingConnectionCubes[0].connectionName]));
    }
  }

  private connectArticleOnRail(
    containers: ArticleContainer[],
    baseContainers: ArticleContainer[],
    articleLine: Line,
    acceptingCube: Connection,
    givingCube: Connection,
    rotationAngle: number,
    excludedUsageIds: string[]
  ): void {
    // get accepting area connection free space rectangle
    const acceptingRectangle: Rectangle3D = this.lineComponentUtils.getConnectionRectangleByConnector(acceptingCube, excludedUsageIds, true, false);
    if (acceptingRectangle.point1.x < acceptingRectangle.point4.x) {
      MathUtils.rotateRectangle(acceptingRectangle, 180);
    }
    const relativeOffset: number = this.containerService.calculateConnectionOffset(
      new Line(acceptingRectangle.point1, acceptingRectangle.point4),
      articleLine.start,
      excludedUsageIds
    );
    MathUtils.interpolateRectangle(acceptingRectangle, relativeOffset);

    const givingRectangle: Rectangle3D = this.connectionInfoService.calculateRectangleByConnection(givingCube, true);
    const rotMatrix: Matrix4 = MathUtils.getRotationMatrix(givingCube.compositionRectangle.toFace(), acceptingRectangle.toFace());
    let givingTopLeftPoint: Vector3 = MathUtils.copyNumber3D(givingRectangle.point1);
    givingTopLeftPoint = MathUtils.rotate([givingTopLeftPoint], rotMatrix)[0];
    givingTopLeftPoint = acceptingRectangle.point1.clone().sub(givingTopLeftPoint);

    ConnectArticleHelper.connectArticleContainers(containers, rotMatrix, givingTopLeftPoint);

    if (!containers) {
      return;
    }
    const childContainers: ArticleContainer[] = this.findChildrenForBaseContainers(baseContainers, containers);
    this.rotateBaseContainers(baseContainers, rotationAngle, childContainers);
  }

  protected dispatchMessages(givingContainers: ArticleContainer[], acceptingCube: Connection): void {
    const acceptingArticleContainer: ArticleContainer = this.containerService.getArticleContainer(acceptingCube);
    const compositionContainer: CompositionContainer = this.containerService.getCompositionContainer(acceptingArticleContainer);
    for (const container of givingContainers) {
      const data: AttachedArticleContainer = {
        articleGUID: container.articleGUID,
        compositionId: compositionContainer.compositionGuid,
        container: container,
        connectedTo: ArticleUsageUtils.getBackwardConnectedConnectionUsageIds(container.articleUsage),
      };

      this.containerService.attachArticleContainer(data)
    }
  }

  private findChildrenForBaseContainers(baseContainers: ArticleContainer[], containers: ArticleContainer[]): ArticleContainer[] {
    const childContainers: ArticleContainer[] = containers.concat();
    for (const baseContainer of baseContainers) {
      if (childContainers.indexOf(baseContainer) > -1) {
        childContainers.splice(childContainers.indexOf(baseContainer), 1);
      }
    }
    return childContainers;
  }

  private rotateBaseContainers(baseContainers: ArticleContainer[], defaultRotationAngle: number, childContainers: ArticleContainer[]): void {
    if (!baseContainers || baseContainers.length === 0) {
      return;
    }

    const rotationAngle: number = NumberUtils.isNotNumber(defaultRotationAngle) ? 0 : defaultRotationAngle;
    const baseContainer: ArticleContainer = baseContainers[0];
    let appliedRotationAngle: number = ArticleUsageUtils.getArticleUsagePlausiPropertyIntValue(
      baseContainer.articleUsage,
      ArticleUsagePlausiPropertyDef.APPLIED_ROTATION_ANGLE
    );
    if (NumberUtils.isNotNumber(appliedRotationAngle)) {
      appliedRotationAngle = 0;
    }
    const isRotated: boolean = this.rotateArticlePresenter.rotateArticleContainersAsGroup(
      baseContainers,
      rotationAngle,
      false,
      false,
      false,
      childContainers
    );

    if (isRotated && !NumberUtils.equals(rotationAngle, appliedRotationAngle)) {
      const additionalContainersRotation: number = Math.abs(appliedRotationAngle - rotationAngle);
      for (const container of baseContainers) {
        ArticleUsageUtils.updateAppliedRotationAngle(container.articleUsage, additionalContainersRotation);
      }
    }
  }
}
